import request from 'api/requestUtils'
import type { paths } from '@luxuryescapes/contract-trip'
import { baseUri } from './utils'

type ReconcileCartOperation =
  paths['/api/trips/v3/chats/{chatId}/reconcile-cart']['post']
type ContractReconcileCartRequest =
  ReconcileCartOperation['requestBody']['content']['application/json']
type ContractReconcileCartResponse =
  ReconcileCartOperation['responses']['200']['content']['application/json']
type ContractReconcileCartResult = ContractReconcileCartResponse['result']
type ContractReconcileCartErrorResponse =
  | ReconcileCartOperation['responses']['401']['content']['application/json']
  | ReconcileCartOperation['responses']['403']['content']['application/json']
  | ReconcileCartOperation['responses']['409']['content']['application/json']
  | ReconcileCartOperation['responses']['502']['content']['application/json']

export type ReconciledCartItem = ContractReconcileCartResult['reconciled'][number]

export interface ReconcileCartResult {
  multiCartId: string | undefined
  previousMultiCartId: string | undefined
  createdMultiCart: boolean
  replacedMultiCart: boolean
  reconciled: Array<ReconciledCartItem>
}

export type ReconcileCartErrorResponse = ContractReconcileCartErrorResponse

type ReconcileCartErrorStatus = ReconcileCartErrorResponse['status']

function hasReconcileCartErrorStatus(status: unknown): status is ReconcileCartErrorStatus {
  return status === 401 || status === 403 || status === 409 || status === 502
}

function hasReconcileCartErrorCode(code: unknown, status: ReconcileCartErrorStatus): boolean {
  if (status === 409) return code === 'RECONCILE_CART_CONFLICT' || code === 'ASSISTANT_TURN_ACTIVE'
  if (status === 502) return code === 'RECONCILE_CART_DEPENDENCY_FAILED'
  return code === undefined
}

export function isReconcileCartErrorResponse(error: unknown): error is ReconcileCartErrorResponse {
  if (!error || typeof error !== 'object') return false
  if (!('status' in error) || !hasReconcileCartErrorStatus(error.status)) return false
  if (error.status === 401 || error.status === 403) return true
  if (!('code' in error) || !hasReconcileCartErrorCode(error.code, error.status)) return false
  if (!('message' in error) || typeof error.message !== 'string') return false
  return 'requiresRefetch' in error && error.requiresRefetch === true
}

type ReconcileCartErrorCode = Extract<ReconcileCartErrorResponse, { code: string }>['code']

export function isReconcileCartErrorCode(error: unknown, code: ReconcileCartErrorCode): boolean {
  return isReconcileCartErrorResponse(error) && 'code' in error && error.code === code
}

function mapReconcileCartResponse(response: ContractReconcileCartResponse): ReconcileCartResult {
  return {
    multiCartId: response.result.multiCartId ?? undefined,
    previousMultiCartId: response.result.previousMultiCartId,
    createdMultiCart: response.result.createdMultiCart,
    replacedMultiCart: response.result.replacedMultiCart,
    reconciled: response.result.reconciled,
  }
}

export async function reconcileCartItems(
  chatId: number,
  itemIds?: Array<number>,
): Promise<ReconcileCartResult> {
  const body: ContractReconcileCartRequest = itemIds ? { itemIds } : {}
  const response = await request.post<ContractReconcileCartResponse, ContractReconcileCartRequest>(
    `${baseUri}/v3/chats/${chatId}/reconcile-cart`,
    body,
    { credentials: 'include' },
  )

  return mapReconcileCartResponse(response)
}
